import axios from "axios";
import moment from "moment";
import {
  setToken,
  readToken,
  getToken,
  clearToken,
  validateToken,
} from "./AuthService";
import { MakeCancelable } from "./CancelablePromise";

/**
 * Handles API access to DE.
 * Builds http requests via axios.
 * Adds Authorization headers and http errors interceptors.
 * Converts http requests to cancelable promises to allow cancellation of promises for unmounted components.
 */

const TOKEN_NAME = "de-token";
const END_POINT = process.env.REACT_APP_DEE_URL;

var instance = axios.create({
  baseURL: END_POINT,
  headers: { common: { Authorization: `Bearer ${getToken(TOKEN_NAME)}` } },
});

registerInterceptor(instance);

// Add a response interceptor for error handling
function registerInterceptor(instance) {
  instance.interceptors.response.use(
    function (response) {
      // Success
      return Promise.resolve(response.data);
    },
    function (error) {
      // Do something with response error
      // @TODO --reauth
      if (error.response) {
        // The request was made and the server responded with a status code
        return Promise.reject({
          data: error.response.data,
          status: error.response.status,
          statusText: error.response.statusText,
        });
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        return Promise.reject({
          data: "",
          status: 0,
          statusText: "No Response",
        });
      } else {
        // Something happened in setting up the request that triggered an Error
        return Promise.reject({
          data: "",
          status: 0,
          statusText: error.message,
        });
      }
    }
  );
}

function isLoggedIn() {
  if (validateToken(TOKEN_NAME)) return readToken(TOKEN_NAME);
  else return false;
}

function isBDCLoggedIn() {
  let isBDC = false;
  if (getToken("is_bdc_loggedIn") !== undefined) {
    isBDC = getToken("is_bdc_loggedIn") === "y" ? true : false;
  }
  return isBDC;
}

function isClientLoggedIn() {
  let isClient = false;
  if (getToken("is_client_loggedIn") !== undefined) {
    isClient = getToken("is_client_loggedIn") === "y" ? true : false;
  }
  return isClient;
}

function isPgmLoggedIn() {
  let ispgm = false;
  if (getToken("is_pgm_loggedIn") !== undefined) {
    ispgm = getToken("is_pgm_loggedIn") === "y" ? true : false;
  }
  return ispgm;
}

function login(data) {
  const options = {
    headers: {
      Authorization: `Bearer ${getToken("bee-token")}`,
    },
  };
  return instance.post("/login", data, options).then((response) => {
    setToken(TOKEN_NAME, response.data && response.data.token);
    instance = axios.create({
      baseURL: END_POINT,
      headers: {
        common: { Authorization: `Bearer ${getToken(TOKEN_NAME)}` },
      },
    });
    registerInterceptor(instance);
    return Promise.resolve({ data: readToken(TOKEN_NAME) });
  });
}

function logout() {
  clearToken(TOKEN_NAME);
  clearToken("is_bdc_loggedIn");
  return Promise.resolve({ data: { message: "success" } });
}

function get(url, data) {
  return MakeCancelable(instance.get(url, data));
}

function post(url, data) {
  return MakeCancelable(instance.post(url, data));
}

function put(url, data) {
  return MakeCancelable(instance.put(url, data));
}

function destroy(url, data) {
  return MakeCancelable(instance.delete(url, data));
}

function download(url, data) {
  return MakeCancelable(
    instance.get(
      url,
      Object.assign(
        {
          responseType: "blob",
          contentType: "application/octet-stream",
        },
        data
      )
    )
  );
}

function syncedCalDate(date) {
  localStorage.setItem("synced_date", moment(date).format("YYYY-MM-DD"));
}

export {
  isLoggedIn,
  login,
  logout,
  post,
  get,
  put,
  destroy,
  download,
  isBDCLoggedIn,
  syncedCalDate,
  isClientLoggedIn,
  isPgmLoggedIn,
  END_POINT,
  getToken,
  TOKEN_NAME,
};
